import React, { useState } from 'react';
import { useToast } from './Toast';
import { apiUrl } from '../config/api';
import { csrfHeaders } from '../utils/csrf';
import { ScanResult } from '../types';
import { ZipAnalysisView } from './ZipAnalysisView';
import { NetworkBreakdown } from './NetworkBreakdown';
import { BackupAdTimeline } from './BackupAdTimeline';
import { CheckDetailModal } from './CheckDetailModal';
import { HistoricalComparison } from './HistoricalComparison';


interface ResultsProps {
  result: ScanResult;
  onReset: () => void;
}

// Complete Advalify check definitions (42 checks)
interface Check {
  id: string;
  label: string;
  category: 'performance' | 'structure' | 'security' | 'compatibility' | 'quality';
  getValue: (result: ScanResult) => CheckResult;
}

interface CheckResult {
  status: 'pass' | 'fail' | 'warn' | 'info' | 'unknown';
  value: string | number;
  message?: string;
  remediation?: string;
}

// Circular Gauge Component
function CircularGauge({ passRate, passCount, failCount, warnCount }: { passRate: number; passCount: number; failCount: number; warnCount: number }) {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (passRate / 100) * circumference;

  const getColor = () => {
    if (passRate >= 80) return '#10b981';
    if (passRate >= 50) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx="60"
          cy="60"
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="8"
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx="60"
          cy="60"
          r={radius}
          stroke={getColor()}
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 1.5s ease-out',
            transformOrigin: '60px 60px'
          }}
        />
        {/* Center text */}
        <text
          x="60"
          y="60"
          textAnchor="middle"
          dy="0.3em"
          style={{
            fontSize: '32px',
            fontWeight: 700,
            fill: '#ffffff',
            transform: 'rotate(90deg)',
            transformOrigin: '60px 60px'
          }}
        >
          {passRate}%
        </text>
      </svg>
      <div style={{ textAlign: 'center', fontSize: '13px' }}>
        <div style={{ color: '#ffffff', fontWeight: 600, marginBottom: '4px' }}>
          {passCount} passed
        </div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <span style={{ color: '#ffffff' }}>{failCount} failed</span>
          <span>·</span>
          <span style={{ color: '#ffffff' }}>{warnCount} warnings</span>
        </div>
      </div>
    </div>
  );
}

const ALL_CHECKS: Check[] = [
  // STRUCTURE CHECKS
  {
    id: 'index-file',
    label: 'Index File Check',
    category: 'structure',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'structure-001');
      return finding
        ? { status: 'warn', value: 'No', message: finding.message, remediation: finding.remediation }
        : { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'backup-ad',
    label: 'Backup Ad',
    category: 'structure',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'structure-005');
      return finding
        ? { status: finding.severity, value: 'No', message: finding.message, remediation: finding.remediation }
        : { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'dimensions',
    label: 'Dimensions',
    category: 'structure',
    getValue: (r) => {
      // Prefer metadata dimensions (parsed from meta ad.size), fall back to runtime screenshot
      const width = r.metadata?.width || r.runtime?.screenshot?.width;
      const height = r.metadata?.height || r.runtime?.screenshot?.height;
      if (width && height) {
        return { status: 'pass', value: `${width}x${height}` };
      }
      return { status: 'unknown', value: '-' };
    }
  },
  {
    id: 'file-types',
    label: 'File Types Allowed',
    category: 'structure',
    getValue: (r) => {
      const finding = r.findings.find(f => f.code === 'STATIC-INVALID-EXTENSION');
      return finding
        ? { status: 'warn', value: 'No', message: finding.message }
        : { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'hosted-files',
    label: 'Hosted File Count',
    category: 'structure',
    getValue: (r) => {
      const count = r.metadata.fileCount;
      const limit = r.profileSnapshot.limits.filesMax;
      return count > limit
        ? { status: 'warn', value: count, message: `Exceeds limit of ${limit}` }
        : { status: 'pass', value: count };
    }
  },
  {
    id: 'hosted-size',
    label: 'Hosted File Size',
    category: 'structure',
    getValue: (r) => {
      // Hosted size = sum of UNCOMPRESSED file sizes (informational, not compared to ZIP limit)
      const totalBytes = r.metadata.extractedFilesDetails?.reduce((sum, f) => sum + f.size, 0) || 0;
      const sizeKb = Math.round(totalBytes / 1024);
      return { status: 'info', value: `${sizeKb} KB` };
    }
  },
  {
    id: 'compressed-size',
    label: 'Compressed File Size',
    category: 'structure',
    getValue: (r) => {
      const sizeKb = Math.round(r.metadata.zipSize / 1024);
      return { status: 'info', value: `${sizeKb} KB` };
    }
  },

  // PERFORMANCE CHECKS
  {
    id: 'load-initial',
    label: 'Load Size: Initial',
    category: 'performance',
    getValue: (r) => { 
      const rawBytes = r.runtime?.performance?.htmlDocumentBytes ?? r.runtime?.performance?.initialLoadBytes ?? 0;
      const sizeKb = (rawBytes / 1024).toFixed(1);
      const limit = r.profileSnapshot.limits.initialKbMax;
      const size = parseFloat(sizeKb);
      return size > limit
        ? { 
            status: 'warn', 
            value: `${sizeKb} KB`, 
            message: `Exceeds ${limit} KB limit`,
            remediation: `Reduce initial load size to ${limit} KB or less. Optimize images, minify code, and defer non-critical assets.`
          }
        : { status: 'pass', value: `${sizeKb} KB` };
    }
  },
  {
    id: 'load-subload',
    label: 'Load Size: Subload',
    category: 'performance',
    getValue: (r) => {
      const sizeKb = r.runtime?.performance?.subloadBytes
        ? (r.runtime.performance.subloadBytes / 1024).toFixed(1)
        : '0.0';
      return { status: 'info', value: `${sizeKb} KB` };
    }
  },
  {
    id: 'load-total',
    label: 'Load Size: Total',
    category: 'performance',
    getValue: (r) => {
      const sizeKb = r.runtime?.performance?.totalLoadBytes
        ? (r.runtime.performance.totalLoadBytes / 1024).toFixed(1)
        : '0.0';
      return { status: 'info', value: `${sizeKb} KB` };
    }
  },
  {
    id: 'load-time',
    label: 'DOMContentLoaded',
    category: 'performance',
    getValue: (r) => {
      const ms = r.runtime?.performance?.domContentLoadedMs || 0;
      return { status: 'info', value: `${ms} ms` };
    }
  },
  {
    id: 'visual-start',
    label: 'Time to Visual Start',
    category: 'performance',
    getValue: (r) => {
      const ms = r.runtime?.performance?.firstContentfulPaintMs || 0;
      return { status: 'info', value: `${ms} ms` };
    }
  },
  {
    id: 'animation-duration',
    label: 'Animation Duration',
    category: 'performance',
    getValue: (r) => {
      const ms = r.runtime?.performance?.animationDurationMs || 0;
      const limit = r.profileSnapshot.limits.animationDurationMs;
      const sec = (ms / 1000).toFixed(1);
      // Only show if detected, otherwise blank
      if (ms === 0) return { status: 'info', value: '-' };
      return ms > limit
        ? { status: 'warn', value: `${sec} sec`, message: `Exceeds ${limit/1000}s` }
        : { status: 'info', value: `${sec} sec` };
    }
  },
  {
    id: 'cpu-usage',
    label: 'CPU Usage',
    category: 'performance',
    getValue: (r) => {
      const ms = r.runtime?.performance?.cpuUsageMs || 0;  // Fixed field name
      // Show as info (blank bubble) not pass
      return ms > 0 
        ? { status: 'info', value: `${ms} ms` }
        : { status: 'info', value: '0 ms' };
    }
  },
  {
    id: 'memory',
    label: 'Memory Usage',
    category: 'performance',
    getValue: (r) => {
      const mb = r.runtime?.performance?.memoryUsageMb || 0;
      return { status: 'info', value: `${mb.toFixed(2)} MB` };
    }
  },
  {
    id: 'network-requests',
    label: 'Network Requests',
    category: 'performance',
    getValue: (r) => {
      const count = r.runtime?.performance?.totalRequests || 0;
      const limit = r.profileSnapshot.limits.requestsMax;
      return count > limit
        ? { status: 'warn', value: count, message: `High request count (limit: ${limit})` }
        : { status: 'pass', value: count };
    }
  },
  {
    id: 'missing-assets',
    label: 'Missing Assets',
    category: 'performance',
    getValue: (r) => {
      const count = r.runtime?.performance?.missing404Assets || 0;
      return count > 0
        ? { status: 'warn', value: count, message: `${count} 404 errors detected` }
        : { status: 'pass', value: 0 };
    }
  },

  // QUALITY CHECKS
  {
    id: 'click-through',
    label: 'Click Through',
    category: 'quality',
    getValue: (r) => {
      const finding = r.findings.find(f => f.code === 'STATIC-NO-CLICKTAG' || f.ruleId === 'html-001');
      const clickTag = r.runtime?.clickTest?.clickTagFound;
      if (finding) return { status: finding.severity, value: 'Missing', message: finding.message, remediation: finding.remediation };
      if (clickTag === false) return { status: 'warn', value: 'Missing' };
      // Use 'info' not 'pass' for blank bubble
      return { status: 'pass', value: 'clickTag' };
    }
  },
  {
    id: 'meta-adsize',
    label: 'Meta tag ad.size',
    category: 'quality',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'html-002' || f.ruleId === 'meta-001' || f.code === 'STATIC-NO-META-ADSIZE');
      return finding
        ? { status: 'warn', value: 'No', message: finding.message, remediation: finding.remediation }
        : { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'css-minified',
    label: 'CSS/JS Minified',
    category: 'quality',
    getValue: (r) => {
      const cssFindings = r.findings.filter(f => f.ruleId === 'css-001' || f.ruleId === 'js-001');
      if (cssFindings.length > 0) {
        const files = cssFindings.map(f => f.message.split(': ')[1] || 'file').join(', ');
        return { 
          status: cssFindings[0].severity, 
          value: `No (${cssFindings.length} files)`, 
          message: `${cssFindings.length} files not minified: ${files}`,
          remediation: cssFindings[0].remediation 
        };
      }
      return { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'images-optimized',
    label: 'Images Optimized',
    category: 'quality',
    getValue: (r) => {
      const findings = r.findings.filter(f => f.ruleId === 'img-001' || f.ruleId === 'img-002');
      const notOptimized = findings.filter(f => f.code === 'STATIC-IMAGE-NOT-OPTIMIZED');
      
      if (notOptimized.length > 0) {
        // Just show "No" like Advalify, not count
        return { 
          status: notOptimized[0].severity, 
          value: 'No',
          remediation: notOptimized[0].remediation
        };
      }
      
      return { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'css-embedded',
    label: 'CSS Embedded',
    category: 'quality',
    getValue: (r) => {
      const finding = r.findings.find(f => f.code === 'STATIC-EXTERNAL-CSS');
      return finding
        ? { status: finding.severity, value: 'No', message: finding.message, remediation: finding.remediation }
        : { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'syntax-errors',
    label: 'Syntax Errors',
    category: 'quality',
    getValue: (r) => {
      const finding = r.findings.find(f => f.code === 'STATIC-HTML-SYNTAX');
      if (finding) {
        // Extract count from message
        const match = finding.message.match(/(\d+) issues/);
        const count = match ? parseInt(match[1]) : 1;
        return { status: finding.severity, value: count, message: finding.message, remediation: finding.remediation };
      }
      return { status: 'pass', value: 0 };
    }
  },
  {
    id: 'console-errors',
    label: 'Console Errors',
    category: 'quality',
    getValue: (r) => {
      const count = r.runtime?.consoleErrors?.errorCount || 0;
      return count > 0
        ? { status: 'warn', value: count, message: `${count} console errors` }
        : { status: 'pass', value: 0 };
    }
  },
  {
    id: 'console-warnings',
    label: 'Console Warnings',
    category: 'quality',
    getValue: (r) => {
      const count = r.runtime?.consoleErrors?.warnings?.length || 0;
      return { status: count > 0 ? 'warn' : 'pass', value: count };
    }
  },

  // SECURITY & COMPATIBILITY
  {
    id: 'ssl-compatible',
    label: 'SSL-Compatibility',
    category: 'security',
    getValue: (r) => {
      const finding = r.findings.find(f => f.code === 'HTTPS-REQUIRED' || f.category === 'security');
      const httpRequests = r.runtime?.performance?.httpRequests;
      const hasHttp = httpRequests && httpRequests.length > 0;
      if (finding) return { status: finding.severity, value: 'No', message: finding.message, remediation: finding.remediation };
      if (hasHttp) return { status: 'warn', value: 'No', message: `${httpRequests.length} HTTP resources detected` };
      return { status: 'pass', value: 'Yes' };
    }
  },
  {
    id: 'external-assets',
    label: 'External Assets',
    category: 'security',
    getValue: (r) => {
      const count = r.runtime?.performance?.externalAssets?.length || 0;
      return { status: count > 0 ? 'info' : 'pass', value: count > 0 ? 'Yes' : 'No' };
    }
  },
  {
    id: 'uses-jquery',
    label: 'Uses jQuery',
    category: 'compatibility',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'html-008' || f.ruleId === 'html-009' || f.code === 'STATIC-USES-JQUERY' || f.code === 'STATIC-JQUERY-DETECTED');
      return finding
        ? { status: 'info', value: 'Yes' }
        : { status: 'pass', value: 'No' };
    }
  },
  {
    id: 'html5-library',
    label: 'HTML5 Library',
    category: 'compatibility',
    getValue: (r) => {
      // Check static finding first (most reliable)
      const finding = r.findings.find(f => f.ruleId === 'html-012');
      if (finding) {
        // Extract library name from evidence
        const lib = finding.evidence || finding.message.replace('HTML5 libraries detected: ', '');
        return { status: 'info', value: lib.includes('GreenSock') ? 'GreenSock' : lib };
      }

      // Fall back to runtime external assets and file list
      const externalAssets = r.runtime?.performance?.externalAssets || [];
      const files = r.metadata?.extractedFiles || [];
      const gsapExternal = externalAssets.some(a => a.includes('gsap') || a.includes('greensock'));
      const gsapLocal = files.some(f => f.toLowerCase().includes('gsap') || f.toLowerCase().includes('greensock'));
      if (gsapExternal || gsapLocal) return { status: 'info', value: 'GreenSock' };
      return { status: 'info', value: '-' };
    }
  },
  {
    id: 'has-video',
    label: 'Has Video',
    category: 'compatibility',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'html-010' || f.code === 'STATIC-VIDEO-DETECTED');
      return finding
        ? { status: 'info', value: 'Yes' }
        : { status: 'pass', value: 'No' };
    }
  },
  {
    id: 'iframe-count',
    label: 'Iframe Count',
    category: 'security',
    getValue: (r) => {
      const count = r.runtime?.iframeCount || 0;
      return { status: count > 1 ? 'warn' : 'pass', value: count };
    }
  },
  {
    id: 'local-storage',
    label: 'Local Storage',
    category: 'security',
    getValue: (r) => {
      const count = r.runtime?.localStorage?.length || 0;
      return { status: count > 0 ? 'warn' : 'pass', value: count };
    }
  },
  {
    id: 'cookies',
    label: 'Cookies Dropped',
    category: 'security',
    getValue: (r) => {
      const count = r.runtime?.cookies?.length || 0;
      return { status: count > 0 ? 'warn' : 'pass', value: count };
    }
  },
  {
    id: 'document-write',
    label: 'Uses document.write()',
    category: 'security',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'html-007' || f.code === 'STATIC-DOCUMENT-WRITE');
      const runtimeUses = r.runtime?.performance?.usesDocumentWrite || false;
      if (finding) return { status: finding.severity, value: 'Yes', message: finding.message, remediation: finding.remediation };
      if (runtimeUses) return { status: 'warn', value: 'Yes', message: 'document.write() detected' };
      return { status: 'pass', value: 'No' };
    }
  },

  // INTERVENTION CHECKS
  {
    id: 'heavy-ad',
    label: 'Heavy Ad Intervention',
    category: 'performance',
    getValue: (r) => {
      const risk = r.runtime?.performance?.heavyAdRisk || 'low';
      if (risk === 'high') return { status: 'warn', value: 'High Risk', message: 'May trigger Heavy Ad intervention' };
      if (risk === 'medium') return { status: 'warn', value: 'Medium Risk' };
      return { status: 'pass', value: 'No' };
    }
  },
  {
    id: 'adblock-risk',
    label: 'Blocked by AdBlock',
    category: 'security',
    getValue: (r) => {
      const risk = r.runtime?.adBlockRisk || 'low';
      if (risk === 'high') return { status: 'warn', value: 'High Risk' };
      if (risk === 'medium') return { status: 'warn', value: 'Medium Risk' };
      return { status: 'pass', value: 'No' };
    }
  },
  {
    id: 'dialogs',
    label: 'Dialogs and Modals',
    category: 'quality',
    getValue: (r) => {
      const count = r.runtime?.dialogCount || 0;
      return count > 0
        ? { status: 'warn', value: count, message: `${count} dialogs detected` }
        : { status: 'pass', value: 0 };
    }
  },
  {
    id: 'creative-border',
    label: 'Creative Border',
    category: 'quality',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'html-005');
      return finding
        ? { status: 'info', value: 'Yes' }
        : { status: 'info', value: 'No' };
    }
  },
  {
    id: 'creative-rendered',
    label: 'Creative Rendered',
    category: 'quality',
    getValue: (r) => {
      // If runtime didn't run at all, show as unknown (not fail)
      if (!r.runtime || r.runtime.outcome === 'skipped') {
        return { status: 'unknown', value: 'No', remediation: undefined };
      }
      const rendered = r.runtime?.screenshot?.base64 ? true : false;
      return {
        status: rendered ? 'pass' : 'fail',
        value: rendered ? 'Yes' : 'No',
        remediation: rendered ? undefined : 'Check HTML structure and JavaScript errors. Ensure creative loads properly in browser environment.'
      };
    }
  },
  {
    id: 'measurement-pixels',
    label: 'Measurement Pixels',
    category: 'quality',
    getValue: (r) => {
      const finding = r.findings.find(f => f.ruleId === 'html-006' || f.code === 'STATIC-MEASUREMENT-PIXELS');
      if (finding) {
        const count = finding.evidence?.split(',').length || 1;
        return { status: finding.severity, value: count, message: finding.message };
      }
      return { status: 'pass', value: 0 };
    }
  },
  {
    id: 'landing-page',
    label: 'Landing Page',
    category: 'quality',
    getValue: (r) => {
      const landingPage = r.runtime?.performance?.landingPageValid;
      if (!landingPage) return { status: 'info', value: 'N/A', message: 'No clickTag destination URL found to validate' };
      return landingPage.isValid
        ? { status: 'pass', value: 'Yes' }
        : { 
            status: 'warn', 
            value: 'No', 
            message: landingPage.error || 'Landing page unavailable',
            remediation: 'Verify the clickTag destination URL is accessible. Check for broken links, invalid domains, or server issues. Test the landing page in a browser to ensure it loads correctly.'
          };
    }
  },
  {
    id: 'manifest',
    label: 'Manifest.json',
    category: 'structure',
    getValue: (r) => {
      const hasManifest = r.findings.find(f => f.ruleId === 'structure-006');
      const invalidManifest = r.findings.find(f => f.ruleId === 'manifest-005');
      const incompleteManifest = r.findings.find(f => f.ruleId === 'manifest-001');
      if (invalidManifest) return { status: 'warn', value: 'Invalid', message: invalidManifest.message, remediation: invalidManifest.remediation };
      if (incompleteManifest) return { status: 'warn', value: 'Incomplete', message: incompleteManifest.message, remediation: incompleteManifest.remediation };
      if (hasManifest) return { status: 'pass', value: 'Yes' };
      return { status: 'info', value: 'None' };
    }
  },
  {
    id: 'duplicate-resources',
    label: 'Duplicate Resources',
    category: 'performance',
    getValue: (r) => {
      const dupes = r.findings.filter(f => f.ruleId === 'perf-001');
      if (dupes.length > 0) return { status: 'warn', value: `${dupes.length} found`, message: dupes[0].message, remediation: dupes[0].remediation };
      return { status: 'pass', value: 'None' };
    }
  },
  {
    id: 'oversized-images',
    label: 'Oversized Images',
    category: 'quality',
    getValue: (r) => {
      const oversized = r.findings.filter(f => f.ruleId === 'img-003');
      if (oversized.length > 0) {
        const worst = oversized[0];
        return { status: 'warn', value: `${oversized.length} found`, message: worst.message, remediation: worst.remediation };
      }
      return { status: 'pass', value: 'None' };
    }
  }
];

export function ResultsEnhanced({ result, onReset }: ResultsProps) {
  const { summary, metadata, profile, runtime } = result;

  // Debug logging for screenshots
  React.useEffect(() => {
    if (result.runtime?.screenshot?.base64) {
      console.log('✓ Screenshot available');
    } else {
      console.log('✗ No screenshot in runtime data');
      console.log('Runtime data:', result.runtime);
    }
  }, [result]);

  // State for check detail modal
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [selectedCheckResult, setSelectedCheckResult] = useState<CheckResult | null>(null);

  // State for collapsible metrics
  const [hideBlankMetrics, setHideBlankMetrics] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { toast } = useToast();

  // Calculate pass rate for circular gauge (only scored checks: pass/fail/warn)
  const passedCount = ALL_CHECKS.filter(check => check.getValue(result).status === 'pass').length;
  const failedCount = ALL_CHECKS.filter(check => check.getValue(result).status === 'fail').length;
  const warnedCount = ALL_CHECKS.filter(check => check.getValue(result).status === 'warn').length;
  const scoredCount = passedCount + failedCount + warnedCount;
  const passRate = scoredCount > 0 ? Math.round((passedCount / scoredCount) * 100) : 0;
  
  // Share URL functionality
  const shareUrl = `${window.location.origin}/scans/${result.scanId}`;
  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    toast('Share URL copied to clipboard!', 'success');
  };

  const exportPDF = async () => {
    setPdfLoading(true);
    try {
      const response = await fetch(apiUrl(`/reports/pdf/${result.scanId}`), {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("PDF generation failed");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `validation-report-${result.scanId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast('PDF downloaded!', 'success');
    } catch (err) {
      console.error("PDF download failed:", err);
      toast("PDF download failed. Please try again.", 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadJSON = () => {
    const dataStr = JSON.stringify(result, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `validation-result-${result.scanId}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast('JSON downloaded!', 'success');
  };

    const emailReport = async () => {
    // Check if logged in
    let userEmail: string | null = null;
    try {
      const res = await fetch(apiUrl('/auth/me'), { credentials: 'include' });
      const data = await res.json();
      if (data.authenticated) userEmail = data.email;
    } catch {}

    let targetEmail = userEmail;
    
    // Prompt if not logged in
    if (!targetEmail) {
      targetEmail = prompt('Enter email address:');
      if (!targetEmail?.includes('@')) {
        toast('Valid email required', 'warning');
        return;
      }
    }

    // Send via API
    try {
      const res = await fetch(apiUrl('/reports/email'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          scanId: result.scanId,
          email: userEmail ? undefined : targetEmail
        })
      });

      const data = await res.json();
      if (res.ok) {
        toast(`Report sent to ${targetEmail}`, 'success');
      } else {
        throw new Error(data.message || 'Failed');
      }
    } catch (error) {
      toast('Failed: ' + (error instanceof Error ? error.message : 'Unknown error'), 'error');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass': return '✓';
      case 'fail': return '✗';
      case 'warn': return '⚠';
      case 'info': return '○';
      default: return '○';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass': return '#10b981';
      case 'fail': return '#ef4444';
      case 'warn': return '#f59e0b';
      case 'info': return '#6b7280';
      default: return '#6b7280';
    }
  };

  // Group checks by category
  const checksByCategory = ALL_CHECKS.reduce((acc, check) => {
    if (!acc[check.category]) acc[check.category] = [];
    acc[check.category].push(check);
    return acc;
  }, {} as Record<string, Check[]>);

  // Combine Security and Compatibility into one card
  const securityChecks = checksByCategory['security'] || [];
  const compatibilityChecks = checksByCategory['compatibility'] || [];
  const combinedSecurityCompatibility = [...securityChecks, ...compatibilityChecks];
  
  // Create display categories with combined security/compatibility
  const displayCategories: Record<string, Check[]> = {
    structure: checksByCategory['structure'] || [],
    performance: checksByCategory['performance'] || [],
    quality: checksByCategory['quality'] || [],
    'security & compatibility': combinedSecurityCompatibility
  };

  const failedChecks = ALL_CHECKS.filter(check => {
    const checkResult = check.getValue(result);
    return checkResult.status === 'fail' && checkResult.remediation;
  });

  return (
    <div className="results-enhanced">
      {/* Header with Circular Gauge */}
      <div className={`results-header ${summary.pass ? 'pass' : 'fail'}`}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '24px', alignItems: 'start', marginBottom: '16px' }}>
          <div>
            <h2>{summary.pass ? '✓ Validation Passed' : `✗ ${summary.failCount} ${summary.failCount === 1 ? 'Test' : 'Tests'} Failed`}</h2>
            {metadata?.filename && (
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '12px' }}>
                {metadata.filename}{metadata.dimensions ? ` — ${metadata.dimensions}` : ''}
              </div>
            )}
            <div className="results-meta">
              <span>Profile: <strong>{profile}</strong></span>
              <span>Files: <strong>{metadata.fileCount}</strong></span>
              <span>Size: <strong>{Math.round(metadata.zipSize / 1024)}KB</strong></span>
              <span>Duration: <strong>{metadata.durationMs}ms</strong></span>
            </div>
          </div>
          <CircularGauge
            passRate={passRate}
            passCount={passedCount}
            failCount={summary.failCount}
            warnCount={summary.warnCount}
          />
        </div>
      </div>
      
      {/* Share URL Section */}
      <div className="share-section" style={{
        padding: '16px',
        backgroundColor: 'var(--badge-pass-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontWeight: 500, color: '#16a34a' }}>✓ Scan Complete</span>
          <span style={{ color: 'rgba(255,255,255,0.7)' }}>|</span>
          <input 
            type="text"
            value={shareUrl}
            readOnly
            style={{
              flex: 1,
              padding: '8px 12px',
              border: '1px solid var(--input-border)',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)'
            }}
            onClick={(e) => e.currentTarget.select()}
          />
          <button
            onClick={copyShareUrl}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px'
            }}
          >
            📋 Copy Link
          </button>
        </div>
        <p style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: 0 }}>
          This link is valid for 30 days and can be shared with your team
        </p>
      </div>

      {/* Visual Preview with Interactive Controls */}
      {runtime?.screenshot?.base64 && (
        <div className="visual-preview">
          <h3>Creative Preview</h3>
          <div className="preview-controls">
            <button className="screenshot-btn" onClick={() => {
              if (runtime?.screenshot?.base64) {
                const link = document.createElement('a');
                link.href = `data:image/jpeg;base64,${runtime.screenshot.base64}`;
                link.download = `creative-${result.scanId}.jpg`;
                link.click();
                toast('Screenshot downloaded!', 'success');
              }
            }}>⬇ Download Screenshot</button>
            <button className="live-preview-btn" onClick={() => {
              window.open(apiUrl(`/scans/${result.scanId}/preview`), '_blank', 'width=800,height=600');
            }}>↗ Open Live Preview</button>
            {runtime?.clickTest?.destinationUrl && (
              <>
                <button className="test-click-btn" onClick={() => {
                  if (runtime.clickTest?.destinationUrl) {
                    window.open(runtime.clickTest.destinationUrl, '_blank');
                  }
                }}>Test Click</button>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginTop: '8px' }}>
                  Destination: <a href={runtime.clickTest.destinationUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>{runtime.clickTest.destinationUrl}</a>
                </div>
              </>
            )}
          </div>
          <div className="preview-container">
            <img 
              src={`data:image/jpeg;base64,${runtime.screenshot.base64}`}
              alt="Creative preview"
              style={{
                maxWidth: `${runtime.screenshot.width || 300}px`,
                border: '1px dashed #ccc',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
            <div className="preview-dimensions">
              {metadata?.dimensions || `${runtime.screenshot.width}x${runtime.screenshot.height}`}
              {metadata?.dimensions && metadata.dimensions !== `${runtime.screenshot.width}x${runtime.screenshot.height}` && (
                <span style={{ fontSize: '11px', opacity: 0.6, marginLeft: '8px' }}>(screenshot: {runtime.screenshot.width}x{runtime.screenshot.height})</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live Preview (when no runtime screenshot but dimensions available) */}
      {!runtime?.screenshot?.base64 && metadata?.dimensions && (
        <div className="visual-preview">
          <h3>Creative Preview</h3>
          <div className="preview-controls">
            <button className="live-preview-btn" onClick={() => {
              window.open(apiUrl(`/scans/${result.scanId}/preview`), '_blank',
                `width=${(metadata.width || 300) + 40},height=${(metadata.height || 250) + 40}`);
            }}>Open Live Preview</button>
          </div>
          <div className="preview-container" style={{ position: 'relative', display: 'inline-block' }}>
            <iframe
              src={apiUrl(`/scans/${result.scanId}/preview`)}
              style={{
                width: `${metadata.width || 300}px`,
                height: `${metadata.height || 250}px`,
                border: '1px dashed #ccc',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
              sandbox="allow-scripts allow-same-origin"
              title="Creative preview"
            />
            <div className="preview-dimensions" style={{
              textAlign: 'center',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.7)',
              marginTop: '8px'
            }}>
              {metadata.width}x{metadata.height}
            </div>
          </div>
        </div>
      )}

      {/* Things to Fix Section */}
      {failedChecks.length > 0 && (
        <div className="things-to-fix">
          <h3>{failedChecks.length} {failedChecks.length === 1 ? 'Thing' : 'Things'} To Fix</h3>
          {failedChecks.map(check => {
            const checkResult = check.getValue(result);
            return (
              <div key={check.id} className="fix-item">
                <div className="fix-header">
                  <span className="fix-icon" style={{ color: getStatusColor(checkResult.status) }}>
                    {getStatusIcon(checkResult.status)}
                  </span>
                  <strong>{check.label}</strong>
                </div>
                {checkResult.remediation && (
                  <p className="fix-remediation">{checkResult.remediation}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Complete Checks Grid */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Validation Checks</h3>
        <button
          onClick={() => setHideBlankMetrics(!hideBlankMetrics)}
          style={{
            padding: '6px 12px',
            backgroundColor: hideBlankMetrics ? '#3b82f6' : '#f3f4f6',
            color: hideBlankMetrics ? '#fff' : '#374151',
            border: '1px solid ' + (hideBlankMetrics ? '#3b82f6' : '#d1d5db'),
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500
          }}
        >
          {hideBlankMetrics ? 'Show All Metrics' : 'Hide Blank Metrics'}
        </button>
      </div>
      <div className="checks-grid">
        {Object.entries(displayCategories).map(([category, checks]) => {
          // Filter checks if hideBlankMetrics is enabled
          const filteredChecks = hideBlankMetrics 
            ? checks.filter(check => {
                const checkResult = check.getValue(result);
                // Hide info-level checks with blank/zero values
                if (checkResult.status !== 'info') return true;
                const blankValues = ['-', '0', 'No', '0.0 KB', '0 ms', '0.00 MB'];
                return !blankValues.includes(String(checkResult.value));
              })
            : checks;
          
          // Don't render empty categories
          if (filteredChecks.length === 0) return null;
          
          return (
          <div key={category} className="checks-category">
            <h3 className="category-title">{category.toUpperCase()}</h3>
            <div className="checks-list">
              {filteredChecks.map((check, index) => {
                const checkResult = check.getValue(result);
                // Add divider between Security and Compatibility
                const isSecurityCompatibilityDivider = category === 'security & compatibility' && 
                  index === securityChecks.length;
                
                return (
                  <React.Fragment key={check.id}>
                    {isSecurityCompatibilityDivider && (
                      <div className="category-divider">
                        <span>COMPATIBILITY</span>
                      </div>
                    )}
                    <div 
                      className="check-row"
                      onClick={() => {
                        setSelectedCheckId(check.id);
                        setSelectedCheckResult(checkResult);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="check-label">
                        <span 
                          className="check-status"
                          style={{ color: getStatusColor(checkResult.status) }}
                        >
                          {getStatusIcon(checkResult.status)}
                        </span>
                        {check.label}
                      </div>
                      <div className="check-value">
                        {checkResult.value}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );})}
      </div>

      {/* ZIP Analysis Section */}
      {result.metadata?.extractedFilesDetails && result.metadata.extractedFilesDetails.length > 0 && (
        <ZipAnalysisView files={result.metadata.extractedFilesDetails} />
      )}

      {/* Network Breakdown Section */}
      {(result.runtime?.performance?.requestDetails || result.runtime?.performance?.networkRequests) && (
        <NetworkBreakdown requests={result.runtime?.performance?.requestDetails || result.runtime?.performance?.networkRequests} />
      )}

      

      {/* Backup Ad Timeline Section */}
      {result.runtime?.performance?.backupTimeline && result.runtime.performance.backupTimeline.length > 0 && (
        <BackupAdTimeline 
          frames={result.runtime.performance.backupTimeline}
          totalDuration={15000}
        />
      )}

      {/* Historical Comparison */}
      <HistoricalComparison />

      {/* Actions */}
      <div className="results-actions">
        <button onClick={exportPDF} className="primary" disabled={pdfLoading}>
          {pdfLoading ? "Generating PDF..." : "PDF Report"}
        </button>
        <button onClick={downloadJSON} className="secondary">
          Download JSON
        </button>
        <button onClick={emailReport} className="secondary">
          Email Report
        </button>
        <button onClick={onReset} className="secondary">
          Validate Another Creative
        </button>
      </div>

      {/* Check Detail Modal */}
      {selectedCheckId && selectedCheckResult && (
        <CheckDetailModal
          checkId={selectedCheckId}
          checkResult={selectedCheckResult}
          onClose={() => {
            setSelectedCheckId(null);
            setSelectedCheckResult(null);
          }}
        />
      )}
    </div>
  );
}
